'use client';

/**
 * /pipeline — the single monitoring home for the visualizer.
 *
 * Steve directive 2026-05-21: "the pipeline page is the monitoring home. all
 * the steps and processes are on this page. it has architecture, health,
 * protection, to-come as sub-headings." Sub-tab count went 7 → 4 in the
 * 2026-05-21 restructure (DATA / FACTORS / VISUALIZER / PROJECTS folded into
 * ARCHITECTURE).
 */

import { useMemo } from 'react';
import { AppNav } from '@/components/AppNav';
import { PageTabs, useUrlTab } from '@/components/PageTabs';
import { useResource } from '@/lib/use-resource';
import type { Surface } from '@/lib/data-surfaces';
import { ArchitectureTab } from './_components/ArchitectureTab';
import { HealthTab } from './_components/HealthTab';
import { TocomeTab } from './_components/TocomeTab';
import { ProtectionTab } from './_components/ProtectionTab';
import { BacktestTab } from './_components/BacktestTab';
import { HealthBanners } from './_components/HealthBanners';
import { DataIntegrityBanner } from './_components/DataIntegrityBanner';

const PIPELINE_TABS: Surface[] = [
  {
    id: 'architecture',
    label: 'Architecture · the live INPUTS → PROCESSES → OUTPUTS map (default tab, 2026-05-21)',
    tabLabel: 'Architecture',
    kind: 'tab',
    href: '/pipeline',
    artifacts: [
      'data/pipeline-status.jsonl',
      'data/audit-history',
      'data/build-protection.jsonl',
      'data/scores.json',
    ],
  },
  {
    id: 'health',
    label: 'Code Health · weekly audit (Sat 23:37): god files, fat routes, ad-hoc fetches',
    tabLabel: 'Health',
    kind: 'tab',
    href: '/pipeline',
    artifacts: ['data/code-health.json'],
    staleAfterDays: 8,
  },
  {
    id: 'protection',
    label: 'Build Protection · day-over-day regression tracker + self-heal + iMessage escalation',
    tabLabel: 'Protection',
    kind: 'tab',
    href: '/pipeline',
    artifacts: ['data/build-protection.jsonl', 'data/audit-history'],
  },
  {
    id: 'tocome',
    label: 'To-Come · candidate universe additions >$5B not yet scored',
    tabLabel: 'To-Come',
    kind: 'tab',
    href: '/pipeline',
    artifacts: ['data/universe-candidates.json'],
    refreshCommand:
      'npx tsx --env-file=.env.local scripts/data-pipeline/build-universe-candidates.ts',
  },
  {
    id: 'backtest',
    label: 'Backtest · factor IC, quintile returns, and Alpha-Zone-vs-Rest across 12 vintages',
    tabLabel: 'Backtest',
    kind: 'tab',
    href: '/pipeline',
    artifacts: ['data/backtest.json'],
    refreshCommand: 'npm run backtest',
  },
];

interface PipelineSummary {
  ok: number;
  queued: number;
  stale: number;
  fail: number;
  total: number;
  running: boolean;
  totalPoints: number;
}

interface PipelineStatusResponse {
  today: string;
  pulls: Array<{ id: string; category?: string }>;
  runs: Array<{
    date: string;
    pulls: Record<string, { status: 'ok' | 'fail' | 'queued' | 'stale' | 'dead'; dataPoints: number | null }>;
  }>;
  running: boolean;
  lastCompletedAt?: string | null;
  nextScheduledAt?: string | null;
}

function fmtTs(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

export default function PipelinePage() {
  const [tab, setTab] = useUrlTab(PIPELINE_TABS, 'architecture');
  const { data: pipelineRaw } = useResource<PipelineStatusResponse>('/api/pipeline-status');

  const summary: PipelineSummary | null = useMemo(() => {
    if (!pipelineRaw) return null;
    const todayRun = pipelineRaw.runs.find((r) => r.date === pipelineRaw.today);
    if (!todayRun) return null;
    let ok = 0,
      queued = 0,
      stale = 0,
      fail = 0,
      totalPoints = 0;
    for (const p of pipelineRaw.pulls) {
      const r = todayRun.pulls[p.id];
      if (!r) continue;
      if (r.status === 'ok') ok += 1;
      else if (r.status === 'queued') queued += 1;
      else if (r.status === 'stale') stale += 1;
      else if (r.status === 'fail') fail += 1;
      if (p.category !== 'momentum' && r.dataPoints != null) totalPoints += r.dataPoints;
    }
    return {
      ok,
      queued,
      stale,
      fail,
      total: pipelineRaw.pulls.length,
      running: pipelineRaw.running === true,
      totalPoints,
    };
  }, [pipelineRaw]);

  return (
    <div className="min-h-screen bg-[#0a0e14] text-white flex flex-col">
      <AppNav />
      <main className="flex-1 px-6 pb-6">
        <div className="max-w-6xl mx-auto">
          <div className="sticky top-[36px] z-40 bg-[#0a0e14] -mx-6 px-6 pt-2 pb-2 border-b border-[#1e293b] shadow-[0_4px_8px_-4px_rgba(0,0,0,0.4)]">
            <div className="flex items-baseline gap-3 flex-wrap">
              <h1 className="text-xl font-bold tracking-tight">Daily Pipeline</h1>
              {summary?.running && (
                <span className="inline-flex items-center gap-1 text-[10px] font-semibold tracking-wider uppercase text-amber-300 bg-amber-400/10 border border-amber-400/30 rounded px-1.5 py-0.5">
                  <span className="w-1 h-1 rounded-full bg-amber-300 animate-pulse" />
                  Refreshing
                </span>
              )}
              <div className="text-[10px] text-[#64748b] tabular-nums">
                {pipelineRaw?.lastCompletedAt && (
                  <>
                    Last <span className="text-[#cbd5e1]">{fmtTs(pipelineRaw?.lastCompletedAt)}</span>
                  </>
                )}
                {pipelineRaw?.nextScheduledAt && (
                  <> · Next <span className="text-[#cbd5e1]">{fmtTs(pipelineRaw?.nextScheduledAt)}</span></>
                )}
              </div>
              {summary && (
                <div className="text-[10px] text-[#94a3b8] ml-auto">
                  <span className="text-emerald-300 font-semibold">{summary.ok} ok</span>
                  {summary.queued > 0 && (
                    <span className="text-amber-300 font-semibold ml-2">· {summary.queued} queued</span>
                  )}
                  {summary.stale > 0 && (
                    <span className="text-amber-300/80 font-semibold ml-2">· {summary.stale} stale</span>
                  )}
                  {summary.fail > 0 && (
                    <span className="text-red-300 font-semibold ml-2">· {summary.fail} fail</span>
                  )}
                  <span className="text-[#64748b] ml-1">/ {summary.total}</span>
                </div>
              )}
            </div>

            {summary && (summary.running || summary.queued > 0) && (
              <div className="mt-1.5 flex items-center gap-3">
                <div className="flex-1 h-2 rounded-full bg-slate-700/40 overflow-hidden">
                  <div
                    className="h-full bg-amber-300 transition-[width] duration-500 ease-out shadow-[0_0_8px_rgba(252,211,77,0.6)]"
                    style={{ width: `${Math.round((summary.ok / Math.max(1, summary.total)) * 100)}%` }}
                  />
                </div>
                <span className="text-[11px] font-mono tabular-nums text-amber-300 font-semibold">
                  {Math.round((summary.ok / Math.max(1, summary.total)) * 100)}% complete
                </span>
                <span className="text-[10px] text-[#94a3b8] tabular-nums">
                  {summary.ok}/{summary.total}
                </span>
              </div>
            )}

            {summary && summary.fail > 0 && !summary.running && (
              <div
                role="alert"
                className="mt-1 inline-flex items-center gap-1.5 px-2 py-0.5 rounded border border-red-400/60 bg-red-400/10 text-[10px] text-red-200/90"
              >
                <span className="w-1 h-1 rounded-full bg-red-400 animate-pulse" aria-hidden />
                <span className="font-semibold uppercase tracking-wide text-red-200">
                  {summary.fail} silent failure{summary.fail === 1 ? '' : 's'}
                </span>
                <span className="text-red-100/70">· see Architecture tab</span>
              </div>
            )}

            <div className="mt-1.5">
              <PageTabs surfaces={PIPELINE_TABS} active={tab} onChange={setTab} />
            </div>
          </div>

          <div className="mt-3 mb-3">
            <DataIntegrityBanner />
            <HealthBanners />
          </div>

          {tab === 'health' && <HealthTab />}
          {tab === 'protection' && <ProtectionTab />}
          {tab === 'tocome' && <TocomeTab />}
          {tab === 'backtest' && <BacktestTab />}
          {/* Architecture is default. Unknown ?tab=… values fall through here too. */}
          {(tab === 'architecture' ||
            (tab !== 'health' && tab !== 'protection' && tab !== 'tocome' && tab !== 'backtest')) && (
            <ArchitectureTab />
          )}
        </div>
      </main>
    </div>
  );
}
